import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Gamepad2, LogIn, Sparkles } from 'lucide-react';
import { api, getStoredToken, saveSession } from '../../api/client';
import type { KorvexGame, SessionUser } from '../../types';

const USERNAME_RULE =
  '3–24 characters. Letters, numbers, underscores, and hyphens only. No , . \' @ # ~ ] } { [ = +';

export function EmbedClientPage() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [games, setGames] = useState<KorvexGame[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.documentElement.classList.add('dark');
    void (async () => {
      try {
        const { user: me } = await api.me();
        setUser(me);
        const { games: list } = await api.getPlayableGames();
        setGames(list);
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    window.korvexAuthToken = () => getStoredToken();
    window.korvexPlayGame = (gameId: string) => {
      window.dispatchEvent(new CustomEvent('korvex:play', { detail: { gameId } }));
    };
    return () => {
      delete window.korvexPlayGame;
      delete window.korvexAuthToken;
    };
  }, []);

  async function handleAuth(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      const result =
        mode === 'login'
          ? await api.login(username.trim(), password)
          : await api.register(username.trim(), password);
      saveSession(result.token);
      setUser(result.user);
      const { games: list } = await api.getPlayableGames();
      setGames(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed.');
    }
  }

  function playGame(gameId: string) {
    window.korvexPlayGame?.(gameId);
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-korvex-bg">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-korvex-accent border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-korvex-bg p-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="panel-card w-full max-w-md">
          <div className="mb-6 flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-korvex-accent to-korvex-violet">
              <Gamepad2 className="h-6 w-6 text-slate-950" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold">Korvex Client</h1>
              <p className="text-sm text-korvex-muted">Sign in to playkorvex.com</p>
            </div>
          </div>
          <form onSubmit={handleAuth} className="space-y-4">
            <input className="input-field" placeholder="Username" value={username} onChange={(e) => setUsername(e.target.value)} required />
            <input type="password" className="input-field" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} />
            {mode === 'register' && <p className="text-xs text-korvex-muted">{USERNAME_RULE}</p>}
            {error && <p className="text-sm text-red-400">{error}</p>}
            <button type="submit" className="btn-primary w-full">
              <LogIn className="h-4 w-4" />
              {mode === 'login' ? 'Sign in' : 'Create account'}
            </button>
          </form>
          <p className="mt-4 text-center text-sm text-korvex-muted">
            {mode === 'login' ? 'New here?' : 'Have an account?'}{' '}
            <button type="button" className="text-korvex-accent hover:underline" onClick={() => setMode(mode === 'login' ? 'register' : 'login')}>
              {mode === 'login' ? 'Register' : 'Sign in'}
            </button>
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-korvex-bg p-6">
      <div className="mx-auto max-w-3xl">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold">Play</h1>
            <p className="text-korvex-muted">Signed in as {user.username}</p>
          </div>
          <Sparkles className="h-8 w-8 text-korvex-accent" />
        </div>
        {games.length === 0 ? (
          <div className="panel-card text-center text-korvex-muted">No games available. Check back when creators publish!</div>
        ) : (
          <div className="space-y-3">
            {games.map((game) => (
              <button
                key={game.id}
                type="button"
                onClick={() => playGame(game.id)}
                className="panel-card flex w-full items-center justify-between text-left transition hover:scale-[1.01] hover:shadow-glow"
              >
                <div>
                  <p className="font-display text-lg font-semibold">{game.name}</p>
                  <p className="text-sm text-korvex-muted">by {game.ownerUsername}</p>
                  <p className="mt-1 line-clamp-1 text-sm text-korvex-muted">{game.description}</p>
                </div>
                <span className="btn-primary shrink-0">Play</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
